import { useState } from 'react';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { daysAgoIso, startOfTodayIso } from '../lib/format';
import type {
  AppSummary,
  Platform,
  SubscriptionEventType,
  UserFilter,
} from '../lib/types';
import { Button, Field, Input, Select, TriStateSelect, cn } from './ui';

/**
 * One-click audiences. Each is only ever a combination of the same generic
 * filter fields — nothing here is a special case in the backend, so a preset
 * and a hand-built filter select exactly the same devices.
 */
const PRESETS: { id: string; label: string; patch: UserFilter }[] = [
  { id: 'today', label: 'Registered today', patch: { registered_from: startOfTodayIso() } },
  { id: 'week', label: 'Last 7 days', patch: { registered_from: daysAgoIso(7) } },
  { id: 'active', label: 'Active today', patch: { last_seen_from: startOfTodayIso() } },
  { id: 'premium', label: 'Subscribed', patch: { is_premium: true } },
  {
    id: 'purchased-today',
    label: 'Purchased today',
    patch: {
      subscription_event: 'purchase',
      subscription_event_from: startOfTodayIso(),
    },
  },
  { id: 'free', label: 'Not subscribed', patch: { is_premium: false } },
  { id: 'gift', label: 'Has gift', patch: { is_gift: true } },
  { id: 'trial', label: 'On trial', patch: { is_trial: true } },
  { id: 'fcm', label: 'Has FCM token', patch: { has_fcm_token: true } },
  {
    id: 'connected',
    label: 'Connected an account',
    patch: { has_connected_account: true },
  },
  {
    // The gap in the setup funnel: they started, then never scanned the QR.
    id: 'stalled',
    label: 'Named but never linked',
    patch: { has_named_account: true, has_connected_account: false },
  },
  {
    id: 'no-account',
    label: 'No account yet',
    patch: { has_named_account: false },
  },
];

export function UserFilters({
  value,
  onChange,
  apps,
}: {
  value: UserFilter;
  onChange: (next: UserFilter) => void;
  /** The selected app's registry entry, for the per-package-name filter. */
  apps?: AppSummary;
}) {
  const [expanded, setExpanded] = useState(false);

  const patch = (next: Partial<UserFilter>) => onChange({ ...value, ...next });

  /** A preset is on when every field it sets currently holds its value. */
  const isActive = (preset: UserFilter): boolean =>
    Object.entries(preset).every(
      ([key, expected]) => value[key as keyof UserFilter] === expected,
    );

  const toggle = (preset: UserFilter) => {
    const next = { ...value };
    for (const key of Object.keys(preset) as (keyof UserFilter)[]) {
      delete next[key];
    }
    onChange(isActive(preset) ? next : { ...next, ...preset });
  };

  // `app_key` is owned by the app picker above, never by these controls.
  const activeCount = Object.keys(value).filter(
    (key) => key !== 'app_key' && value[key as keyof UserFilter] !== undefined,
  ).length;

  return (
    <div className="space-y-3 rounded-xl border border-line bg-surface p-3.5 shadow-sm sm:p-4">
      <div className="grid gap-2 sm:grid-cols-[2fr_1fr]">
        <Input
          value={value.search ?? ''}
          onChange={(event) => patch({ search: event.target.value || undefined })}
          placeholder="Search by device id (starts with…)"
          aria-label="Search by device id"
        />
        <Input
          value={value.ip_address ?? ''}
          onChange={(event) =>
            patch({ ip_address: event.target.value.trim() || undefined })
          }
          maxLength={45}
          placeholder="IP address (exact)"
          aria-label="Search by IP address"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => toggle(preset.patch)}
            aria-pressed={isActive(preset.patch)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              isActive(preset.patch)
                ? 'border-brand bg-brand-soft text-brand'
                : 'border-line text-ink-2 hover:border-line-strong hover:bg-raised',
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="ghost"
          className="text-xs"
          icon={<SlidersHorizontal size={14} />}
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide filters' : 'More filters'}
          {activeCount > 0 && (
            <span className="ml-1 rounded-full bg-brand px-1.5 text-[11px] text-on-brand">
              {activeCount}
            </span>
          )}
        </Button>

        {activeCount > 0 && (
          <Button
            variant="ghost"
            className="text-xs"
            icon={<RotateCcw size={14} />}
            // Keeps the app selection; clears everything the operator set.
            onClick={() => onChange({ app_key: value.app_key })}
          >
            Reset
          </Button>
        )}
      </div>

      {expanded && (
        <div className="grid gap-3 border-t border-line pt-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Platform">
            <Select
              value={value.platform ?? ''}
              onChange={(event) =>
                patch({
                  platform: (event.target.value || undefined) as
                    | Platform
                    | undefined,
                })
              }
            >
              <option value="">Any</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
            </Select>
          </Field>

          {apps && apps.package_names.length > 1 && (
            <Field label="Package name">
              <Select
                value={value.app_package_name ?? ''}
                onChange={(event) =>
                  patch({ app_package_name: event.target.value || undefined })
                }
              >
                <option value="">All builds</option>
                {apps.package_names.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label="Language" hint="Two-letter code, e.g. en">
            <Input
              value={value.language ?? ''}
              onChange={(event) =>
                patch({
                  language: event.target.value.trim().toLowerCase() || undefined,
                })
              }
              maxLength={12}
              placeholder="Any"
            />
          </Field>

          <Field label="Country" hint="Two-letter code, e.g. US">
            <Input
              value={value.country ?? ''}
              onChange={(event) =>
                patch({
                  country: event.target.value.trim().toUpperCase() || undefined,
                })
              }
              maxLength={8}
              placeholder="Any"
            />
          </Field>

          <Field label="App version" hint="Exact build, e.g. 1.0.1+7">
            <Input
              value={value.current_app_version ?? ''}
              onChange={(event) =>
                patch({ current_app_version: event.target.value.trim() || undefined })
              }
              maxLength={64}
              placeholder="Any"
            />
          </Field>

          <TriStateSelect
            label="Subscribed"
            value={value.is_premium}
            onChange={(next) => patch({ is_premium: next })}
          />
          <TriStateSelect
            label="Gift"
            value={value.is_gift}
            onChange={(next) => patch({ is_gift: next })}
          />
          <TriStateSelect
            label="Trial"
            value={value.is_trial}
            onChange={(next) => patch({ is_trial: next })}
          />
          <TriStateSelect
            label="Has FCM token"
            value={value.has_fcm_token}
            onChange={(next) => patch({ has_fcm_token: next })}
          />
          <TriStateSelect
            label="Named an account"
            value={value.has_named_account}
            onChange={(next) => patch({ has_named_account: next })}
          />
          <TriStateSelect
            label="Linked an account"
            value={value.has_connected_account}
            onChange={(next) => patch({ has_connected_account: next })}
          />

          <Field label="Registered after">
            <Input
              type="date"
              value={toDateInput(value.registered_from)}
              onChange={(event) =>
                patch({ registered_from: fromDateInput(event.target.value) })
              }
            />
          </Field>
          <Field label="Registered before">
            <Input
              type="date"
              value={toDateInput(value.registered_to)}
              onChange={(event) =>
                patch({ registered_to: fromDateInput(event.target.value) })
              }
            />
          </Field>
          <Field label="Last seen after">
            <Input
              type="date"
              value={toDateInput(value.last_seen_from)}
              onChange={(event) =>
                patch({ last_seen_from: fromDateInput(event.target.value) })
              }
            />
          </Field>

          <Field label="Store event" hint="Has at least one of these">
            <Select
              value={value.subscription_event ?? ''}
              onChange={(event) =>
                patch({
                  subscription_event: (event.target.value || undefined) as
                    | SubscriptionEventType
                    | undefined,
                })
              }
            >
              <option value="">Any</option>
              {SUBSCRIPTION_EVENTS.map((event) => (
                <option key={event.value} value={event.value}>
                  {event.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Store event after">
            <Input
              type="date"
              disabled={!value.subscription_event}
              value={toDateInput(value.subscription_event_from)}
              onChange={(event) =>
                patch({
                  subscription_event_from: fromDateInput(event.target.value),
                })
              }
            />
          </Field>
        </div>
      )}
    </div>
  );
}

const SUBSCRIPTION_EVENTS: { value: SubscriptionEventType; label: string }[] = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'trial', label: 'Trial started' },
  { value: 'restore', label: 'Restore' },
  { value: 'cancel', label: 'Cancellation' },
  { value: 'expire', label: 'Expiration' },
  { value: 'billing_issue', label: 'Billing issue' },
  { value: 'uncancel', label: 'Re-subscribed' },
];

/** ISO instant → the `yyyy-mm-dd` a date input expects, in local time. */
function toDateInput(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

/** `yyyy-mm-dd` → the local midnight instant the API filters on. */
function fromDateInput(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
